/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
*/

import produce from 'immer';
import {error} from '@ion/core/src/logger/logger';
import {searchDeviceCommunication, searchDevices} from '@ion/core/src/services/Connect';
import {
  DeviceActivitySearchTypes,
  DeviceCommunicationSearchTypes,
  DeviceSearchTypes,
  LoadDevicesParams
} from '@ion/core/src/types/services';
import {
  DeviceCommunicationReportType,
  DeviceConfigurationVersionsResponseType,
  DeviceFirmwareVersionsResponseType,
  DeviceResultType,
  TotalCountType
} from '@/types/dashboard';
import {PageState} from '@/types/pages';
import {getTotal, isValidResponse} from './helpers';

// Type of the device redux state
export interface DevicesType {
  result?: DeviceResultType;
  loading: boolean;
  error: boolean;
}

// Type of the deviceCommunication redux state
export interface DeviceCommunicationType {
  result?: DeviceCommunicationReportType;
  loading: boolean;
  error: boolean;
}

// Response type of the device communication search response
export interface DeviceCommunicationResponseType {
  status: number,
  data: {
    response: TotalCountType
  }
}

// Type of the deviceCommunication types request
export interface DeviceCommunicationTypeRequest {
  lastSevenDays?: number;
  lastFourteenDays?: number;
  lastThirtyDays?: number;
  lastNinetyDays?: number;
}

// Device Firmware Versions Redux State Type
export interface DeviceFirmwareVersionsType {
  result?: DeviceFirmwareVersionsResponseType;
  loading: boolean;
  error: boolean;
}

// Device Configurations Versions Redux State Type
export interface DeviceConfigurationVersionsType {
  result?: DeviceConfigurationVersionsResponseType;
  loading: boolean;
  error: boolean;
}

interface IncludeSubGroupsType {
  dashboard: boolean;
  crashbox: boolean;
}

// Drilldown Search Redux State Type
export interface DrillDownDevicesParamType extends LoadDevicesParams {
  searchType: DeviceSearchTypes | DeviceCommunicationSearchTypes | DeviceActivitySearchTypes;
  searchTerm?: string;
}

// defines the type definitions for dashboard reducer ...
export interface DashboardState extends PageState {
  drilldownDrawer: {
    shouldOpen: boolean;
    drilldownSearch: DrillDownDevicesParamType;
  }
  devices: DevicesType;
  deviceCommunication: DeviceCommunicationType;
  firmwareVersions: DeviceFirmwareVersionsType;
  configurationVersions: DeviceConfigurationVersionsType;
  includeSubGroups: IncludeSubGroupsType;
};

// defines the initial state for the reducer ...
export const initialState: DashboardState = {
  isInitialized   : false,
  drilldownDrawer : {
    shouldOpen      : false,
    drilldownSearch : {
      searchType : null,
      searchTerm : null
    }
  },
  devices : {
    result  : null,
    loading : false,
    error   : false
  },
  deviceCommunication : {
    result  : null,
    loading : false,
    error   : false
  },
  firmwareVersions : {
    result  : null,
    loading : false,
    error   : false
  },
  configurationVersions : {
    result  : null,
    loading : false,
    error   : false
  },
  includeSubGroups : {
    dashboard : false,
    crashbox  : false
  }
};

export const TOGGLE_DASHBOARD_DRAWER                    = 'TOGGLE_DASHBOARD_DRAWER';
export const SET_SELECTED_DASHBOARD_TAB                 = 'SET_SELECTED_DASHBOARD_TAB';
export const SET_SEARCH_TYPE                            = 'SET_SEARCH_TYPE';
export const SET_ADDED_WIDGETS                          = 'SET_ADDED_WIDGETS';
export const SET_SHOW_EDIT_WIDGETS                      = 'SET_SHOW_EDIT_WIDGETS';
export const SET_SELECTED_DASHBOARD_PERIOD              = 'SET_SELECTED_DASHBOARD_PERIOD';
export const GET_MY_DEVICES_REQUEST                     = 'GET_MY_DEVICES_REQUEST';
export const GET_MY_DEVICES_RESPONSE                    = 'GET_MY_DEVICES_RESPONSE';
export const GET_MY_DEVICES_ERROR                       = 'GET_MY_DEVICES_ERROR';
export const GET_DEVICE_COMMUNICATION_REQUEST           = 'GET_DEVICE_COMMUNICATION_REQUEST';
export const GET_DEVICE_COMMUNICATION_RESPONSE          = 'GET_DEVICE_COMMUNICATION_RESPONSE';
export const GET_DEVICE_COMMUNICATION_ERROR             = 'GET_DEVICE_COMMUNICATION_ERROR';
export const GET_DEVICE_FIRMWARE_VERSIONS_REQUEST       = 'GET_DEVICE_FIRMWARE_VERSIONS_REQUEST';
export const GET_DEVICE_FIRMWARE_VERSIONS_RESPONSE      = 'GET_DEVICE_FIRMWARE_VERSIONS_RESPONSE';
export const GET_DEVICE_FIRMWARE_VERSIONS_ERROR         = 'GET_DEVICE_FIRMWARE_VERSIONS_ERROR';
export const GET_DEVICE_CONFIGURATION_VERSIONS_REQUEST  = 'GET_DEVICE_CONFIGURATION_VERSIONS_REQUEST';
export const GET_DEVICE_CONFIGURATION_VERSIONS_RESPONSE = 'GET_DEVICE_CONFIGURATION_VERSIONS_RESPONSE';
export const GET_DEVICE_CONFIGURATION_VERSIONS_ERROR    = 'GET_DEVICE_CONFIGURATION_VERSIONS_ERROR';
export const SET_DASHBOARD_VIEW_SUB_GROUPS              = 'SET_DASHBOARD_VIEW_SUB_GROUPS';
export const SET_CRASHBOX_VIEW_SUB_GROUPS               = 'SET_CRASHBOX_VIEW_SUB_GROUPS';

export const DASHBOARD_PREFERENCE = 'overview-dashboard';
export const CRASHBOX_PREFERENCE  = 'crashbox-dashboard';

/**
 * Toggles the dashboard overview drawer open or closed based on the given value.
 */
export const toggleDashboardDrawer = (value: boolean) => ({
  type : TOGGLE_DASHBOARD_DRAWER, value
});

/**
 * Updates the currently selected page index
 */
export const setSelectedTab = (selectedTab: number) => ({
  type : SET_SELECTED_DASHBOARD_TAB, selectedTab
});

/**
 * Sets the drill down search type when a widget's detail (bar, link, pie slice) is clicked so the grid
 * knows what devices to load.
 */
export const setDrillDownSearchType = (searchType, searchTerm = null) => ({
  type : SET_SEARCH_TYPE, searchType, searchTerm
});

/**
 * Invoked whenever user click on Dashboard View Sub Accounts toggle button
 */
export const setDashboardViewSubGroups = (viewSubGroups: boolean = false) => ({
  type : SET_DASHBOARD_VIEW_SUB_GROUPS, viewSubGroups
});

/**
 * Invoked whenever user click on Crashbox View Sub Accounts toggle button
 */
export const setCrashboxViewSubGroups = (viewSubGroups: boolean = false) => ({
  type : SET_CRASHBOX_VIEW_SUB_GROUPS, viewSubGroups
});

/**
 * Retrieves device information from the PULS device search gateway.
 * Used primarily to retrieve total device count, and aggregations for the dashboard.
 */
export const getMyDevices = (accountId: number, hierLevel?: boolean) => {
  return async(dispatch, getState) => {
    try {
      dispatch({type: GET_MY_DEVICES_REQUEST});
      const {data, status} = await searchDevices(
        DeviceSearchTypes.DEVICE_MODEL,
        accountId,
        hierLevel
      );
      if (status === 200 && data) {
        dispatch({type: GET_MY_DEVICES_RESPONSE, response: data.response});
      } else {
        dispatch({type: GET_MY_DEVICES_ERROR});
      }
    } catch (err) {
      error(err);
      dispatch({type: GET_MY_DEVICES_ERROR});
    }
  };
};

/**
 * Retrieves the device communication data for the last 7, 14, 30, and 90 days.
 */
export const getDeviceCommunication = (accountId: number, hierLevel?: boolean) => {
  return async(dispatch, getState) => {
    try {
      dispatch({type: GET_DEVICE_COMMUNICATION_REQUEST});
      const responses = await Promise.all([
        searchDeviceCommunication(
          DeviceCommunicationSearchTypes.LAST_7_DAYS,
          accountId,
          hierLevel
        ),
        searchDeviceCommunication(
          DeviceCommunicationSearchTypes.LAST_14_DAYS,
          accountId,
          hierLevel
        ),
        searchDeviceCommunication(
          DeviceCommunicationSearchTypes.LAST_30_DAYS,
          accountId,
          hierLevel
        ),
        searchDeviceCommunication(
          DeviceCommunicationSearchTypes.LAST_90_DAYS,
          accountId,
          hierLevel
        )]);
      if (isValidResponse(responses)) {
        const [
          lastSevenDaysResponse,
          lastFourteenDays,
          lastThirtyDays,
          lastNinetyDays
        ] = responses;
        const communications: DeviceCommunicationReportType = {
          lastSevenDays    : lastSevenDaysResponse.data.response.totalCount,
          lastFourteenDays : lastFourteenDays.data.response.totalCount,
          lastThirtyDays   : lastThirtyDays.data.response.totalCount,
          lastNinetyDays   : lastNinetyDays.data.response.totalCount,
          totalCount       : getTotal(responses)
        };
        dispatch({type: GET_DEVICE_COMMUNICATION_RESPONSE, response: communications});
      } else {
        dispatch({type: GET_DEVICE_COMMUNICATION_ERROR});
      }
    } catch (err) {
      error(err);
      dispatch({type: GET_DEVICE_COMMUNICATION_ERROR});
    }
  };
};

/**
 * Retrieves device firmware version overview data from the PULS device search gateway.
 */
export const getDeviceFirmwareVersions = (accountId: number, hierLevel?: boolean) => {
  return async(dispatch, getState) => {
    try {
      dispatch({type: GET_DEVICE_FIRMWARE_VERSIONS_REQUEST});
      const {data, status} = await searchDevices(
        DeviceSearchTypes.FIRMWARE_VERSION,
        accountId,
        hierLevel
      );
      if (status === 200 && data) {
        dispatch({type: GET_DEVICE_FIRMWARE_VERSIONS_RESPONSE, response: data.response});
      } else {
        dispatch({type: GET_DEVICE_FIRMWARE_VERSIONS_ERROR});
      }
    } catch (err) {
      error(err);
      dispatch({type: GET_DEVICE_FIRMWARE_VERSIONS_ERROR});
    }
  };
};

/**
 * Retrieves device configuration version overview data from the PULS device search gateway.
 */
export const getDeviceConfigurationVersions = (accountId: number, hierLevel?: boolean) => {
  return async(dispatch, getState) => {
    try {
      dispatch({type: GET_DEVICE_CONFIGURATION_VERSIONS_REQUEST});
      const {data, status} = await searchDevices(
        DeviceSearchTypes.CONFIGURATION_VERSION,
        accountId,
        hierLevel
      );
      if (status === 200 && data) {
        dispatch({type: GET_DEVICE_CONFIGURATION_VERSIONS_RESPONSE, response: data.response});
      } else {
        dispatch({type: GET_DEVICE_CONFIGURATION_VERSIONS_ERROR});
      }
    } catch (err) {
      error(err);
      dispatch({type: GET_DEVICE_CONFIGURATION_VERSIONS_ERROR});
    }
  };
};

// defines this reducers reducer functions ...
const reducers = {
  [TOGGLE_DASHBOARD_DRAWER] : ({drilldownDrawer}: DashboardState, {value}) => {
    drilldownDrawer.shouldOpen = value;
  },
  [SET_DASHBOARD_VIEW_SUB_GROUPS] : ({includeSubGroups}: DashboardState, {viewSubGroups}) => {
    includeSubGroups.dashboard = viewSubGroups;
  },
  [SET_CRASHBOX_VIEW_SUB_GROUPS] : ({includeSubGroups}: DashboardState, {viewSubGroups}) => {
    includeSubGroups.crashbox = viewSubGroups;
  },
  [SET_SEARCH_TYPE] : ({drilldownDrawer: {drilldownSearch}}: DashboardState, {searchType, searchTerm}) => {
    drilldownSearch.searchType = searchType;
    drilldownSearch.searchTerm = searchTerm;
  },
  [GET_MY_DEVICES_REQUEST] : ({devices}: DashboardState) => {
    devices.loading = true;
    devices.error   = false;
  },
  [GET_MY_DEVICES_RESPONSE] : ({devices}: DashboardState, {response}) => {
    devices.loading = false;
    devices.result  = response;
    devices.error   = false;
  },
  [GET_MY_DEVICES_ERROR] : ({devices}: DashboardState) => {
    devices.loading = false;
    devices.error   = true;
  },
  [GET_DEVICE_COMMUNICATION_REQUEST] : ({deviceCommunication}: DashboardState) => {
    deviceCommunication.loading = true;
    deviceCommunication.error   = false;
  },
  [GET_DEVICE_COMMUNICATION_RESPONSE] : ({deviceCommunication}: DashboardState, {response}) => {
    deviceCommunication.loading = false;
    deviceCommunication.result  = response;
    deviceCommunication.error   = false;
  },
  [GET_DEVICE_COMMUNICATION_ERROR] : ({deviceCommunication}: DashboardState) => {
    deviceCommunication.loading = false;
    deviceCommunication.error   = true;
  },
  [GET_DEVICE_FIRMWARE_VERSIONS_REQUEST] : ({firmwareVersions}: DashboardState) => {
    firmwareVersions.loading = true;
    firmwareVersions.error   = false;
  },
  [GET_DEVICE_FIRMWARE_VERSIONS_RESPONSE] : ({firmwareVersions}: DashboardState, {response}) => {
    firmwareVersions.loading = false;
    firmwareVersions.result  = response;
    firmwareVersions.error   = false;
  },
  [GET_DEVICE_FIRMWARE_VERSIONS_ERROR] : ({firmwareVersions}: DashboardState) => {
    firmwareVersions.loading = false;
    firmwareVersions.error   = true;
  },
  [GET_DEVICE_CONFIGURATION_VERSIONS_REQUEST] : ({configurationVersions}: DashboardState) => {
    configurationVersions.loading = true;
    configurationVersions.error   = false;
  },
  [GET_DEVICE_CONFIGURATION_VERSIONS_RESPONSE] : ({configurationVersions}: DashboardState, {response}) => {
    configurationVersions.loading = false;
    configurationVersions.result  = response;
    configurationVersions.error   = false;
  },
  [GET_DEVICE_CONFIGURATION_VERSIONS_ERROR] : ({configurationVersions}: DashboardState) => {
    configurationVersions.loading = false;
    configurationVersions.error   = true;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: PageState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
